import { useState } from 'react';
import { ConfirmDialog, Field, TextArea } from '@zarya/ui';
import { t } from '@app/i18n';

export interface DecisionBoxProps {
  titulo: string;
  obrigatorio: boolean;
  placeholder: string;
  confirmar: string;
  perigo?: boolean;
  enviando: boolean;
  erro?: string | null;
  onCancelar: () => void;
  onConfirmar: (motivo: string) => void;
}

/**
 * Diálogo de decisão (§3.4). Aprovar e rejeitar têm diálogo **próprio** —
 * mesma moldura, textos e obrigatoriedade diferentes (FR-062).
 *
 * Era uma caixa no fim do drawer, que exigia rolar até o rodapé para achar o
 * motivo (ajustes-front #8, 28/08); agora é um modal centralizado sobre a tela,
 * com o mesmo campo e os mesmos botões («Confirmar aprovação» / «Confirmar
 * rejeição» — o E2E depende dos nomes).
 */
export function DecisionBox({
  titulo,
  obrigatorio,
  placeholder,
  confirmar,
  perigo,
  enviando,
  erro,
  onCancelar,
  onConfirmar,
}: DecisionBoxProps) {
  const [motivo, setMotivo] = useState('');
  const [tocado, setTocado] = useState(false);

  const missingReason = obrigatorio && !motivo.trim();
  const errorInline =
    tocado && missingReason ? t('approvals.dialog.reason.required') : (erro ?? null);

  return (
    <ConfirmDialog
      open
      title={titulo}
      destructive={perigo}
      confirmLabel={confirmar}
      loading={enviando}
      onCancel={onCancelar}
      onConfirm={() => {
        setTocado(true);
        if (missingReason) return;
        onConfirmar(motivo);
      }}
      message={
        <Field
          label={t('approvals.dialog.reason.label')}
          required={obrigatorio}
          error={errorInline}
          help={t('approvals.dialog.reason.help')}
        >
          {(props) => (
            <TextArea
              {...props}
              rows={3}
              value={motivo}
              placeholder={placeholder}
              onChange={(e) => setMotivo(e.target.value)}
            />
          )}
        </Field>
      }
    />
  );
}

export interface DialogProps {
  enviando: boolean;
  erro?: string | null;
  onCancelar: () => void;
  onConfirmar: (motivo: string) => void;
}

/** **Aprovar release** — motivo opcional (FR-062). */
export function ApproveDialog(props: DialogProps) {
  return (
    <DecisionBox
      titulo={t('approvals.dialog.approve.title')}
      obrigatorio={false}
      placeholder={t('approvals.dialog.approve.placeholder')}
      confirmar={t('approvals.dialog.approve.confirm')}
      {...props}
    />
  );
}
